import { ProjectManageService } from './project-manage.service';
import { catchError, map, tap } from 'rxjs/operators';
import { Config } from '@shared/configs/config';
import { normalizeApiDatePayload } from '@shared/helpers/date-format.helper';
import { Injectable } from '@angular/core';
import { BaseHttpService } from '@core/http/base-http.service';
import { Project } from '../models/project.model';
import { ACTIVE_USER_ROLES, UserRoles } from '@shared/enums/user-roles.enum';
import { isNotNullOrUndefined } from 'codelyzer/util/isNotNullOrUndefined';
import { Observable, throwError } from 'rxjs';
import { ProjectStage } from '@shared/enums/project-stage.enum';
import { ProjectDataBox } from '../models/project-data-box.model';
import { ProjectStageResponse, TimelineStage } from '../interfaces/timeline.interface';
import { ProjectLogQueryParams, ProjectLogResponse } from '../interfaces/project-log.interface';

export interface ProjectMapListItem {
  id: number;
  stage: string;
  name: string;
  address: string | null;
  town: string | null;
  postalCode: string | null;
  country: string | null;
  projectId: string;
  latitude: number | null;
  longitude: number | null;
}

@Injectable({ providedIn: 'root' })
export class ProjectAPIService extends BaseHttpService {
  constructor(public manager: ProjectManageService) {
    super();
  }

  getProjectShareLink(id: number) {
    const url = `${Config.API}/project-data-box/simple-2/${id}/share-token`;
    return this.post(url, {});
  }

  extendProjectFormToken(projectId: number) {
    const url = `${Config.API}/project/${projectId}/form-token/extend`;
    return this.post(url, {});
  }

  projectWithoutShare(id: number) {
    const url = `${Config.API}/project/${id}/without-sharing`;
    return this.post(url, {});
  }

  createProjectDraft() {
    const url = `${Config.API}/project/draft`;
    return this.post(url, {});
  }

  findProjectByID(id: number) {
    const url = `${Config.API}/project/${id}`;
    return this.get(url).pipe(map((p) => this.manager.successFindProjectByID(p)));
  }

  editProjectDataBox(id: number, data: any) {
    const url = `${Config.API}/project-data-box/basic/${id}`;
    return this.post(url, data).pipe(map((p) => this.manager.successFindProjectByID(p)));
  }

  assignMainContactToProject(projectid: number, contactID: number): Observable<Project> {
    const url = `${Config.API}/project-data-box/basic/${projectid}/main-client/${contactID}`;
    return this.post(url, {}).pipe(map((p) => this.manager.successFindProjectByID(p)));
  }

  assignResponsibleContactToProject(projectid: number, responsibleId: number): Observable<Project> {
    const url = `${Config.API}/project/${projectid}/responsible`;
    return this.post(url, { responsibleId }).pipe(map((p) => this.manager.successFindProjectByID(p)));
  }

  assignMainAndResponsibleContact(projectid: number, data: any): Observable<Project> {
    const url = `${Config.API}/project-data-box/basic/${projectid}/client-in-project`;
    return this.post(url, data).pipe(map((p) => this.manager.successFindProjectByID(p)));
  }

  assignBasicContactToProject(
    projectid: number,
    contactID: number,
    isResponsible: boolean
  ): Observable<Project> {
    const url = `${Config.API}/project-data-box/basic/${projectid}/client/${contactID}`;
    return this.post(url, { isResponsible }).pipe(map((p) => this.manager.successFindProjectByID(p)));
  }

  activateProject(projectid: number) {
    const url = `${Config.API}/project/${projectid}/active`;
    return this.post(url, {});
  }

  addContactToProject(contactId: number, projectId: number, isResponsible?: boolean): Observable<Project> {
    let body = {};
    if (isNotNullOrUndefined(isResponsible)) {
      body['isResponsible'] = isResponsible;
    }
    const url = `${Config.API}/project-data-box/basic/${projectId}/client/${contactId}`;
    return this.post(url, body);
  }

  removeContactFromProject(accessContactId: number) {
    const url = `${Config.API}/contact-project/${accessContactId}`;
    return this.delete(url);
  }

  removeContactFromProjectWithoutAccessContact(contactId: number, projectId: number) {
    const url = `${Config.API}/project/${projectId}/contact/${contactId}`;
    return this.delete(url);
  }

  inviteNewContactToProject(
    contactId: number,
    email: string,
    role: UserRoles,
    hasAccess: boolean = false,
    projectId: number
  ) {
    const url = `${Config.API}/contact/${contactId}/invite-new`;
    const body = {
      email,
      role,
      hasAccess,
      projectId
    };
    return this.post(url, body);
  }

  inviteExistContactToProject(
    contactId: number,
    employeeId: number,
    hasAccess: boolean = false,
    projectId: number,
    isMain: boolean = false
  ) {
    const url = `${Config.API}/contact/${contactId}/invite-existing`;
    const body = {
      employeeId,
      isMain,
      hasAccess,
      projectId
    };
    return this.post(url, body);
  }

  getProject(id: number) {
    const url = `${Config.API}/project/${id}`;
    return this.get(url).pipe(map((p) => new Project(p)));
  }

  getProjectLog(projectId: number, params: ProjectLogQueryParams = {}): Observable<ProjectLogResponse> {
    const url = `${Config.API}/project/${projectId}/log`;
    return this.get(url, false, params) as Observable<ProjectLogResponse>;
  }

  createProjectLogComment(projectId: number, payload: { content: string }): Observable<any> {
    const url = `${Config.API}/project/${projectId}/log`;
    return this.post(url, payload);
  }

  getProjectsMapList(): Observable<ProjectMapListItem[]> {
    const url = `${Config.API}/project/list/map`;
    return this.get(url);
  }

  getProjectsForStage(stage: ProjectStage, onlyFavourite: boolean = false) {
    const url = `${Config.API}/project/stage/${stage}`;
    return this.get(url, false, { onlyFavourite: onlyFavourite ? 1 : 0 }).pipe(
      map((projects: Project[]) => projects.map((p) => new Project(p)))
    );
  }

  editSharedSecondBoxWithToken(token: string, data: any) {
    const url = `${Config.API}/project-data-box/simple-2/${token}`;
    return this.post(url, data);
  }

  getSharedSecondBox(token: string): Observable<ProjectDataBox> {
    const url = `${Config.API}/project-data-box/simple-2/${token}`;
    return this.get(url, true);
  }

  shareSecondBox(projectId: number, email: string) {
    const url = `${Config.API}/project-data-box/simple-2/${projectId}/share`;
    return this.post(url, { email }, false, false);
  }

  shareSecondBoxWithToken(projectId: number, email: string) {
    const url = `${Config.API}/project-data-box/simple-2/${projectId}/share-with-token`;
    return this.post(url, { email }, false, false);
  }

  setAlerts(projectId: number, costsLimit: number[]) {
    const url = `${Config.API}/alert/project-costs-limit`;
    const body = {
      projectId,
      costsLimit
    };
    return this.post(url, body, false, false);
  }

  setFavourite(projectId: number) {
    const url = `${Config.API}/favourite/project/${projectId}`;
    return this.post(url, {}, false, false);
  }

  removeFavourite(projectId: number) {
    const url = `${Config.API}/favourite/project/${projectId}`;
    return this.delete(url);
  }

  setResponsibleEmployee(projectId: number, employeeId: number) {
    const url = `${Config.API}/project-data-box/basic/${projectId}/responsible-employee`;
    let body = {
      employeeId
    };
    return this.post(url, body, false, false);
  }

  removeProject(projectId: number) {
    const url = `${Config.API}/project/${projectId}`;
    return this.delete(url);
  }

  getKonceptPDF(projectId: number) {
    const url = `${Config.API}/project/${projectId}/pdf?timestamp=${Date.now()}`;
    return this.getFileBlob(url, false);
  }

  updateBox2(id: number, data: any) {
    const url = `${Config.API}/project-data-box/simple-2/${id}`;
    return this.post(url, data).pipe(map((p) => this.manager.successFindProjectByID(p)));
  }

  updateBox3(id: number, data: any) {
    const url = `${Config.API}/project-data-box/simple-3/${id}`;
    return this.post(url, data).pipe(map((p) => this.manager.successFindProjectByID(p)));
  }

  getAssignedEmployees(projectId: number) {
    const url = `${Config.API}/project/${projectId}/employees`;
    return this.get(url);
  }

  searchResponsibleEmployees(search: string = '', requiredId?: number) {
    const params = new URLSearchParams();
    ACTIVE_USER_ROLES.forEach((role) => params.append('filters[a.role][in][]', role));

    if (requiredId) params.append('requiredId', String(requiredId));
    if (search?.trim()) params.append('search', search.trim());

    const url = `${Config.API}/employee/search?${params.toString()}`;
    return this.get(url);
  }

  setProjectPauseRange({
    id,
    payload,
    ignoreToast
  }: {
    id: number;
    payload: any;
    ignoreToast?: boolean;
  }): Observable<void> {
    const url = `${Config.API}/project/${id}/timeline`;
    return this.post(url, normalizeApiDatePayload(payload)).pipe(
      tap(() => {
        if (!ignoreToast) this.s.success(this.t.instant('ProjectsTimeline.V2.successProjectPauseChanged'));
      }),
      catchError((err) => {
        this.s.error(this.t.instant('ProjectsTimeline.V2.errorProjectPauseChanged'));
        return throwError(err);
      })
    );
  }

  getProjectStages(projectId: number): Observable<TimelineStage[]> {
    const url = `${Config.API}/project/${projectId}/timeline-stage`;
    return this.get(url);
  }

  reorderProjectStages(projectId: number, stageIds: number[]): Observable<void> {
    const url = `${Config.API}/project/${projectId}/timeline-stage/reorder`;
    return this.patch(url, stageIds).pipe(
      tap(() => {
        this.s.success(this.t.instant('Projects.Stages.reorderSuccess'));
      }),
      catchError((err) => {
        this.s.error(this.t.instant('Projects.Stages.reorderError'));
        return throwError(err);
      })
    );
  }

  createProjectStage(
    projectId: number,
    stage: { name: string; color: string; order: number }
  ): Observable<TimelineStage> {
    const url = `${Config.API}/project/${projectId}/timeline-stage`;
    return this.post(url, stage).pipe(
      tap(() => {
        this.s.success(this.t.instant('Projects.Stages.createSuccess'));
      }),
      catchError((err) => {
        this.s.error(this.t.instant('Projects.Stages.createError'));
        return throwError(err);
      })
    );
  }

  updateProjectStage(
    projectId: number,
    stageId: number | string,
    stage: { name: string; color: string; order: number }
  ): Observable<TimelineStage> {
    const url = `${Config.API}/project/${projectId}/timeline-stage/${stageId}`;
    return this.put(url, stage).pipe(
      tap(() => {
        this.s.success(this.t.instant('Projects.Stages.updateSuccess'));
      }),
      catchError((err) => {
        this.s.error(this.t.instant('Projects.Stages.updateError'));
        return throwError(err);
      })
    );
  }

  deleteProjectStage(projectId: number, stageId: number | string): Observable<void> {
    const url = `${Config.API}/project/${projectId}/timeline-stage/${stageId}`;
    return this.delete(url).pipe(
      map(() => void 0),
      tap(() => {
        this.s.success(this.t.instant('Projects.Stages.deleteSuccess'));
      }),
      catchError((err) => {
        this.s.error(this.t.instant('Projects.Stages.deleteError'));
        return throwError(err);
      })
    );
  }

  getProjectStagesById(id: number): Observable<any> {
    const url = `${Config.API}/project/${id}/timeline`;
    return this.get(url);
  }

  setProjectStage({
    id,
    payload,
    ignoreToast
  }: {
    id: number;
    payload: any;
    ignoreToast?: boolean;
  }): Observable<ProjectStageResponse> {
    const url = `${Config.API}/project/${id}/timeline/stage`;
    return this.post(url, normalizeApiDatePayload(payload)).pipe(
      tap(() => {
        if (!ignoreToast) this.s.success(this.t.instant('ProjectsTimeline.V2.successProjectStageChanged'));
      }),
      catchError((err) => {
        this.s.error(this.t.instant('ProjectsTimeline.V2.errorProjectStageChanged'));
        return throwError(err);
      })
    );
  }

  getHistoryForStage(projectId: number, historyGroupId: number) {
    const url = `${Config.API}/project/${projectId}/timeline/history-group/${historyGroupId}`;
    return this.get(url);
  }

  getProjectsList(params?: any) {
    const url = `${Config.API}/project/search-advanced`;
    return this.get(url, false, params);
  }
}
